import React, { useState, useEffect } from 'react'
import { parseName } from '../../../utils/math-utils'
import { useApi } from '../../../hooks/useApi'
import CustomInput from '../../general/Form'
import styled from 'styled-components'

const URL = process.env.REACT_APP_SERVER_BASE_URL

interface PriceOracleSettingsProps {
  onSuccess?: () => void
}

interface RelayerData {
  _id: string
  rateLimitRequests: number
  rateLimitWindowMs: number
  corsAllowedOrigins: string[]
}

export const PriceOracleSettings: React.FC<PriceOracleSettingsProps> = ({ onSuccess = () => {} }) => {
  const [message, setMessage] = useState('')
  const [priceOracleData, setPriceOracleData] = useState<RelayerData | null>(null)
  const [editedSettings, setEditedSettings] = useState<Partial<RelayerData> | null>(null)
  const { callApi } = useApi()

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await callApi('/price-oracle/settings')
        if (response) {
          setPriceOracleData(response)
          setEditedSettings(response)
        }
      } catch (error) {
        console.error('There was an error with relayer executor form data:', error)
      }
    }

    fetchSettings()
  }, [])

  const handleCancelEditClick = () => {
    setEditedSettings(priceOracleData)
  }

  const handleSaveClick = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const response = await callApi('/price-oracle/settings', editedSettings, 'PUT')
    } catch (error) {
      console.error('Error al guardar las configuraciones de la API:', error)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target

    setEditedSettings((prevSettings: any) => {
      const updatedValue = e.target.type === 'number' ? parseFloat(value) : value

      return {
        ...prevSettings,
        [name]: updatedValue,
      }
    })
  }

  return (
    <Form onSubmit={handleSaveClick}>
      {message !== '' ? (
        <FeedbackBanner>
          <span>{message}</span>
          <CloseBtn onClick={() => setMessage('')}>✕</CloseBtn>
        </FeedbackBanner>
      ) : (
        <>
          <FieldGrid>
            {editedSettings &&
              Object.entries(editedSettings as RelayerData).map(
                ([key, value]) =>
                  !['__v', 'createdAt', 'updatedAt', 'lastUpdate', '_id'].includes(key) && (
                    <div key={key}>
                      <CustomInput
                        type={typeof value === 'number' ? 'number' : 'text'}
                        name={key}
                        label={parseName(key)}
                        value={typeof value === 'number' ? value : String(value)}
                        onChange={handleInputChange}
                      />
                    </div>
                  )
              )}
          </FieldGrid>
          <Actions>
            <SaveBtn type="submit">Save</SaveBtn>
            <CancelBtn type="button" onClick={handleCancelEditClick}>Cancel</CancelBtn>
          </Actions>
        </>
      )}
    </Form>
  )
}

export default PriceOracleSettings

const Form = styled.form`display: flex; flex-direction: column; gap: 16px;`
const FieldGrid = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 8px;`
const Actions = styled.div`display: flex; gap: 8px;`
const SaveBtn = styled.button`height: 34px; padding: 0 16px; background: rgba(111,92,230,0.15); border: 1px solid rgba(111,92,230,0.35); border-radius: 6px; color: #a89aff; font-size: 13px; font-weight: 500; cursor: pointer; transition: background 0.15s; &:hover { background: rgba(111,92,230,0.25); border-color: rgba(111,92,230,0.5); }`
const CancelBtn = styled.button`height: 34px; padding: 0 14px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 6px; color: #777; font-size: 13px; cursor: pointer; &:hover { border-color: rgba(255,255,255,0.15); color: #a0a0a0; }`
const FeedbackBanner = styled.div`display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; background: rgba(51,194,176,0.08); border: 1px solid rgba(51,194,176,0.25); border-radius: 6px; color: #33C2B0; font-size: 13px;`
const CloseBtn = styled.button`background: transparent; border: none; color: inherit; cursor: pointer; font-size: 12px; opacity: 0.7; &:hover { opacity: 1; }`
